import { BondForfeituresComponent } from './bond-forfeitures/bond-forfeitures.component';
import { SafeJSON } from './../shared/safe-json';
import { DatePipe } from '@angular/common';
import { JL } from 'jsnlog';
import { BondsmanIdentifier } from '../shared/bondsman-identifier';

/*
Why Runner is Prohibited section:

FBR619.1 – when license type = runner
a.	doiStatus from bondsmanLicenseTypes section.
b.	agentForStatus from the bondsmanCountyData section
c.	registryStatus from top section data

Why Bail Agent is Prohibited section:

FBR620.1 – when license type = Bail Agent
a.	doiStatus from bondsmanLicenseTypes section.
b.	agentForStatus from the bondsmanCountyData section
c.	registryStatus from top section data

FBR619.2 – when license type = Professional Bondsman
a.	doiStatus from bondsmanLicenseTypes section.
b.	registryStatus from top section data
c.	countyStatus from the bondsmanCountyData section

FBR620.2 – when license type = Surety Insurance Company
a.	doiStatus from bondsmanLicenseTypes section.
b.	registryStatus from top section data
c.	countyStatus from the bondsmanCountyData section

Steve Savino
*/
export class Bondsman {

    json: any;
    bondsmanIdentifier: BondsmanIdentifier;
    searchRecordLastUpdateDate: string = new Date().toJSON();

    public ALL = 'ALL';
    public  PROFESSIONAL_BONDSMAN = 'PROFESSIONAL BONDSMAN';
    public RUNNER = 'RUNNER';
    public BAIL_AGENT = 'BAIL AGENT';
    public SURETY_INSURANCE_COMPANY = 'SURETY INSURANCE COMPANY';
    public ALLOWED = 'ALLOWED';
    public ACTIVE = 'ACTIVE';
    public INACTIVE = 'INACTIVE';
    public PROHIBITED = 'PROHIBITED';
    public NOT_PROHIBITED = 'NOT PROHIBITED';
    public JUDGEMENT_PENDING = 'JUDGEMENT PENDING';
    public JUDGMENT_PENDING = 'JUDGMENT PENDING';
    public FINAL = 'FINAL AND UNSATISFIED';
    public PENDING_JUDGMENTS = 'PENDING';

    constructor(json: any) {
        this.json = json;
        this.bondsmanIdentifier = new BondsmanIdentifier (this.getLicenseNumber());
        this.bondsmanIdentifier.setStateLicenseId(this.getStateLicenseId());
        // view JSON in the console
         console.log('JSON='); console.log(JSON.stringify(this.json));
    }
    public getBondsmanIdentifier(): BondsmanIdentifier {
        return this.bondsmanIdentifier;
    }
    // You can call this method from HTML template code to see the json in your browser
    public getJson(): String {
        return JSON.stringify(this.json);
    }
    public getLicenseeLastName(): string {
        return SafeJSON.get('lastName', this.json);
    }
    public getLicenseeFirstName(): string {
        return SafeJSON.get('firstName', this.json);
    }
    public getLicenseeMiddleName(): string {
        return SafeJSON.get('middleName', this.json);
    }
    public getBusinessName(): string {
        return SafeJSON.get('businessName', this.json);
    }
    public getLicenseNumber(): string {
        return SafeJSON.get('doiLicenseNumber', this.json);
    }
    public getStateLicenseId(): number {
        return SafeJSON.get('stateLicenseId', this.json);
    }
    public getLicenseType(): string {
        return SafeJSON.get('licenseType', this.json);
    }
    /********************************************************************
    RUNNER
       NOT PROHIBITED		300070
       JUDGEMENT PENDING	5308187

    BAIL AGENT
       NOT PROHIBITED		13
       JUDGEMENT PENDING	892
    Returns the employee (runner or bail agent) statewide registry status
    *********************************************************************/
    public getStatewideRegistryStatus(): string {
        const status = SafeJSON.get('stateRegistryStatus', this.json);
        return status;
    }
    // You will only get a date is the status is not active or prohibited
    public getStatewideRegistryStatusDate(): string {
        return SafeJSON.get('stateRegistryStatusDate', this.json);
    }
    public getLicenseStatus(licenseType: string): string {
        const countyDataList: any[] = this.getCountyDataForListByType(licenseType);
        const status = this.getDerivedStatusToPostBondsByProfBondsmanOrInsCo(countyDataList[0]);
        return status;
    }
    public getLicenseStatusDate(licenseType: string): string {
        const countyDataList: any[] = this.getCountyDataForListByType(licenseType);
        return this.getDerivedStatusToPostBondsByProfBondsmanOrInsCoDate(countyDataList[0]);
    }
    /******************************************************************************
     EMPLOYER (Professional Bondsman)----

        registryStatus (county)
        NOT PROHIBITED		200053
        JUDGEMENT PENDING	400051

    EMPLOYER (Insurance Company)-------

        registryStatus (county)
        NOT PROHIBITED		13
        PROHIBITED			17146188

    Returns the status of the listed employer (Professional Bondsman or Insurance Co)
    **********************************************************************************/
    public getCountyRegistryStatus(countyData: string): string {
        const status = SafeJSON.get('registryStatus', countyData);
        return status;
    }
    public getCountyRegistryStatusDate(countyData: string): string {
        const statusDate = SafeJSON.get('registryStatusDate', countyData);
        return statusDate;
    }
    // Get this at the license type role level
    public getProfessionalBondsmanOrInsuranceCompanyProhibitedReasonWhyDoi(licenseType: string): string {
        let status = this.getDoiStatusByType(licenseType);
        if (status === this.PROHIBITED || status === this.INACTIVE) {
            return 'Department of Insurance Status: ' + status + ' since ' +
                         this.convertDate(this.getDoiStatusDateByType(licenseType));
        }
        return '';
    }
    // Get this at the very top license level
    public getProfessionalBondsmanOrInsuranceCompanyProhibitedReasonWhyStatewide(licenseType: string): string {
        const status = this.getStatewideRegistryStatus()
        if (status === this.JUDGEMENT_PENDING || status === this.JUDGMENT_PENDING) {
            return 'Statewide Registry Status: ' + status + ' since ' + this.convertDate(this.getStatewideRegistryStatusDate());
        }
        return '';
    }
    public getProfessionalBondsmanOrInsuranceCompanyProhibitedReasonWhyCounty(licenseType: string): string {
        const countyDatalist: any[] = this.getCountyDataForListByType(licenseType);
        for (let countyData of countyDatalist) {
            const status = this.getCountyStatus(countyData);
            if (status === this.JUDGEMENT_PENDING || status === this.JUDGMENT_PENDING) {
                 return 'County Registry Status: ' + status + ' since ' + this.convertDate(this.getCountyStatusDate(countyData));
            }
        }
        return '';
    }
    public showProfessionalLicenseStatusField(licenseeType: string): boolean {
        const licenseTypeList: any[] = this.getLicenseTypeListUntouched();
        for (let licenseType of licenseTypeList) {
            if (this.getLicenseTypeName(licenseType) === licenseeType) {
              return true;
            }
        }
        return false;
    }
    public isLicenseeThisType(licenseType: string): boolean {
        const licenseTypeList: any[] = this.getLicenseTypeListUntouched();
        for (let licenseTypeFromList of licenseTypeList) {
            if (this.getLicenseTypeName(licenseTypeFromList) === licenseType) {
              return true;
            }
        }
        return false;
    }
    public getLicenseTypeName(type: any): string {
        return SafeJSON.get('licenseType', type);
    }
    public getLicenseCountyDataList(type: any): any[] {
        let countyDataList: any[] = SafeJSON.get('bondsmanCountyData', type);
        return countyDataList;
    }
    public getBondForfeituresUri(): string {
        if (this.json === null || this.json.forfeitureIndexList === undefined || this.json.forfeitureIndexList === null) {
            return '';
        }
        return SafeJSON.get('bondsman forfeitures', this.json).href;
    }
    public getBondForfeitures(): any[] {
        return this.json.forfeitureIndexList;
    }
    public setForfeitures(forfeitures: any[]) {
        this.json.forfeitureIndexList  = forfeitures;
    }
    /*
    UX417
    Display 'Final and Unsatisfied judgments' first followed by the 'Pending judgments'.
        Judgments within each type will be sorted by Judgment Date in chronological order,
        then by County in alphabetical order,
        and then by File Number in ascending order.
    */
   // 3244
    public getBondForfeitureList(bondsman: Bondsman, selectedCounty: string, selectedJudgment: string, selectedLicenseType: string): any[] {
        if (this.json === null || this.json.forfeitureIndexList === undefined || this.json.forfeitureIndexList === null) {
            return [];
        }
        // First apply the column filters set by the user.
        let bondForfeitureList: any[] = Array.from(this.json.forfeitureIndexList.values())
            .filter(forfeiture => bondsman.getForfeitureCounty(forfeiture) === selectedCounty || selectedCounty === this.ALL);
        let bondForfeitureList2: any[] = Array.from(bondForfeitureList.values())
            .filter(forfeiture => bondsman.getForfeitureJudgment(forfeiture) === selectedJudgment || selectedJudgment === this.ALL);
        let bondForfeitureListSelection: any[] = Array.from(bondForfeitureList2.values())
            .filter(forfeiture => bondsman.getForfeitureLicenseType(forfeiture) === selectedLicenseType ||
                                            selectedLicenseType === this.ALL);

        // At the top is the final judgments, so get them first.
        let bondForfeitureListFinalJudgment: any[] = Array.from(bondForfeitureListSelection.values())
            .filter(forfeiture => bondsman.getForfeitureJudgment(forfeiture) === this.FINAL);

        // Now we need to order the list by the judgment date, etc as per the sorting BR above.
        bondForfeitureListFinalJudgment.sort((p1, p2) => {
            let compare = p1.judgmentDate.localeCompare(p2.judgmentDate);
            if (compare !== 0) {
                return compare;
            }
            compare = p1.county.localeCompare(p2.county);
            if (compare !== 0) {
                return compare;
            }
            return p1.caseNumber.localeCompare(p2.caseNumber);
            });

        // At the bottom is the pending judgments, so get them last.
        let bondForfeitureListPendingJudgment: any[] = Array.from(bondForfeitureListSelection.values())
            .filter(forfeiture => bondsman.getForfeitureJudgment(forfeiture) === this.PENDING_JUDGMENTS);

        // Now we need to order the list by the judgment date, etc as per the sorting BR above.
        bondForfeitureListPendingJudgment.sort((p1, p2) => {
            let compare = p1.judgmentDate.localeCompare(p2.judgmentDate);
            if (compare !== 0) {
                return compare;
            }
            compare = p1.county.localeCompare(p2.county);
            if (compare !== 0) {
                return compare;
            }
            return p1.caseNumber.localeCompare(p2.caseNumber);
            });
        return [...bondForfeitureListFinalJudgment, ...bondForfeitureListPendingJudgment];
    }
    public showReasonsWhyBailAgentIsProhibitedColumn(): boolean {

        const countyDataList: any[] = this.getCountyDataForListByType(this.BAIL_AGENT);
        for (let countyData of countyDataList) {
            const bailAgentReasonsList: any [] = this.getBailAgentProhibitReasonsList(countyData);
            if (bailAgentReasonsList.length > 0) {
                return true;
            }
            const insuranceCompanyReasonsList: any [] = this.getInsuranceCompanyProhibitReasonsList(countyData);
            if (insuranceCompanyReasonsList.length > 0) {
                return true;
            }
        }
        // Getting here means we didn't find any prohibitied reasons in any of the county data list.
        return false;
    }
    // Bail agent with no county data: 0017975709
    // Bail agent with no prohibitions: 2428929  (hide the column as per BR 599)
    // Runner with no prohibitions: 0009430666
    public getCountyDataForListByType(type: string): any[] {
        let countyDataList: any[];
        for (let licenseType of this.getLicenseTypeListUntouched()) {
            if (this.getLicenseTypeName(licenseType) === type) {
                countyDataList = this.getLicenseCountyDataList(licenseType);
                // adding the countyDataList check
                if (countyDataList !== null && countyDataList !== undefined && countyDataList.length !== 0 ) {
                    /*
                    The nested sort comes in to play when the current sort callback function determines that
                    the items are equal; then it moves down the chain to the next sort callback.
                    For example, the status's are the same so look at the names for those two items.
                    */
                    countyDataList = Array.from(countyDataList.values());

                    countyDataList.sort( function(p1, p2) {
                        let compare = p1.countyRegistryStatus.localeCompare(p2.countyRegistryStatus);
                        if (compare !== 0) {
                            return compare;
                        }
                        if (p1.licenseType === 'PROFESSIONAL BONDSMAN') {
                            compare = p1.lastName.localeCompare(p2.lastName);
                        } else {
                            compare = p1.businessName.localeCompare(p2.businessName);
                        }
                        return compare;
                    });
                    return Array.from(countyDataList.values());
                } else {
                    return new Array();
                }
            }
        }
        return new Array();
    }
    public getDoiStatusByType(type: string): string {
        for (let licenseType of this.getLicenseTypeListUntouched()) {
            if (this.getLicenseTypeName(licenseType) === type) {
                const status = this.getDoiStatusForStatewide(licenseType);
                return status;
            }
        }
        return '';
    }
    public getDoiStatusDateByType(type: string): string {
        for (let licenseType of this.getLicenseTypeListUntouched()) {
            if (this.getLicenseTypeName(licenseType) === type) {
                const statusDate = this.getDoiStatusDateForStatewide(licenseType);
                return statusDate;
            }
        }
        return '';
    }
    getIsLicenseTypeProhibited(licenseType: string): boolean {
        const countyDataList: any[] = this.getCountyDataForListByType(licenseType);

        let status = this.getDoiStatusByType(licenseType);
        if (status === this.PROHIBITED || status === this.INACTIVE) {
            return true;
        }
        for (let countyData of countyDataList) {
            if (licenseType === this.RUNNER || licenseType === this.BAIL_AGENT) {
                status = this.getAgentForStatus(countyData); // Runner Status with Listed Professional Bondsman
                if (status === this.INACTIVE) {
                    return true;
                }
            }
            if (licenseType === this.PROFESSIONAL_BONDSMAN) {
                status = this.getCountyStatus(countyData);
                if (status === this.JUDGEMENT_PENDING) {
                    return true;
                }
            }
            // common status both Runner and Prof Bondsman share.
            const countyStatus = this.getDoiStatusForCounty(countyData);
            if (countyStatus === this.JUDGEMENT_PENDING) {
                return true;
            }

            const statewideStatus = this.getStatewideRegistryStatus();
            if (statewideStatus === this.JUDGEMENT_PENDING) {
                return true;
            }
        }
        return false;
    }
    public getLicenseTypeListUntouched(): Array<string> {
        if (this.json === null || this.json.bondsmanLicenseTypes === null ) {
            return [];
        }
        return Array.from(this.json.bondsmanLicenseTypes.values());
    }
    public getLicenseTypeList(): Array<string> {
        let orderedLicenseTypeList: any[] = new Array();
        for (let licenseType of this.getLicenseTypeListUntouched()) {
            if (this.getLicenseTypeName(licenseType) === this.PROFESSIONAL_BONDSMAN) {
                orderedLicenseTypeList.push(licenseType);
                break;
            }
        }
        for (let licenseType of this.getLicenseTypeListUntouched()) {
            if (this.getLicenseTypeName(licenseType) === this.RUNNER) {
                orderedLicenseTypeList.push(licenseType);
                break;
            }
        }
        for (let licenseType of this.getLicenseTypeListUntouched()) {
            if (this.getLicenseTypeName(licenseType) === this.BAIL_AGENT) {
                orderedLicenseTypeList.push(licenseType);
                break;
            }
        }
        return orderedLicenseTypeList;
    }
    getLicenseeAddressLine1(): string {
        return SafeJSON.getWithDefault('address.line1', this.json);
    }
    getLicenseeAddressLine2(): string {
        return SafeJSON.get('address.line2', this.json);
    }
    getLicenseeCityName(): string {
        return SafeJSON.get('address.cityName', this.json);
    }
    getLicenseeStateAbbreviation(): string {
        const state = SafeJSON.get('address.state',  this.json);
        if (state !== '') {
            return ', ' + state;
        }
        return '';
    }
    getLicenseeAddressesZipCode(): string {
        const zipCode = SafeJSON.get('address.zipCode', this.json);
        const zipCode4 = SafeJSON.get('address.zip4', this.json);
        if (zipCode4 !== '') {
            return zipCode + '-' + zipCode4;
        }
        return zipCode;
    }
    getLicenseeCounty(): string {
        return SafeJSON.get('address.county', this.json);
    }
    // The county is always in the county data and its the same for all the county data,
    // just get the first county name.
    getLicenseeCountyForTitle(): string {
        const licenseTypeList: any[] = this.getLicenseTypeListUntouched();
        const county: string = SafeJSON.get('countyName', licenseTypeList[0].bondsmanCountyData[0]);
        return county;
    }
    getLicenseeCountyFormatted(): string {
        const county: string = SafeJSON.get('address.county', this.json);
        return this.formatCounty(county);
    }
    formatCounty(county: string): string {
        if (county === null || county === undefined || county.trim() === '') {
            return '';
        }
        return county + ' COUNTY';
    }
    /////////////////////////////////////////////////////////////////////////////
    // Licensee Status for county and statewide
    public getSuretyName(countyData: any): string {
        return SafeJSON.get('name', countyData);
    }
    public getSuretyAddressLine1(countyData: any): string {
        return SafeJSON.getWithDefault('address.line1', countyData);
    }
    public getSuretyAddressLine2(countyData: any): string {
        return SafeJSON.get('address.line2', countyData);
    }
    getSuretyCityName(countyData: any): string {
        return SafeJSON.get('address.cityName', countyData);
    }
    getSuretyCountyName(countyData: any): string {
        return this.formatCounty(SafeJSON.get('address.county', countyData));
    }
    getSuretyStateAbbreviation(countyData: any): string {
        const state = SafeJSON.get('address.state', countyData);
        if (state !== '') {
            return ', ' + state;
        }
        return '';
    }
    getSuretyZipCode(countyData: any): string {
        const zipCode5 = SafeJSON.get('address.zipCode', countyData);
        const zipCode4 = SafeJSON.get('address.zip4', countyData);
        if (zipCode4 !== '') {
            return zipCode5 + '-' + zipCode4;
        }
        return zipCode5;
    }
    public getSuretyLicenseNumberForCounty(countyData: any): string {
        return SafeJSON.get('doiLicenseNumber', countyData);
    }
    /******************************************************
    RUNNER
        NOT PROHIBITED		300070
        JUDGEMENT PENDING	200072

    BAIL AGENT
        NOT PROHIBITED		200053
        JUDGEMENT PENDING	400051

    Returns the county status of employer in that county
    ********************************************************/
    public getCountyStatus(countyData: any): string {
        return SafeJSON.get('countyRegistryStatus', countyData);
    }
    public getCountyStatusDate(countyData: any): string {
        return SafeJSON.get('countyRegistryStatusDate', countyData);
    }
    /*******************************************************************
    EMPLOYER (Professional Bondsman)----
        PROHIBITED			200053
        ACTIVE				79744

    EMPLOYER (Insurance Company)-------
        PROHIBITED			42
        ACTIVE				27

    Returns the department of insurance status of employer in that county
    **********************************************************************/
    public getDoiStatusForCounty(countyData: any): string {
        return SafeJSON.get('doiStatus', countyData);
    }
    // You will only get a date if the status is prohibited or inactive
    public getDoiStatusForCountyDate(countyData: any): string {
        return SafeJSON.get('doiStatusDate', countyData);
    }
    public getBusinessNameFromCounty(countyData: string): string {
        return SafeJSON.get('businessName', countyData);
    }
    public getLicenseeFirstNameFromCounty(countyData: string): string {
        return SafeJSON.get('firstName', countyData);
    }
    public getLicenseeMiddleNameFromCounty(countyData: string): string {
        return SafeJSON.get('middleName', countyData);
    }
    public getLicenseeLastNameFromCounty(countyData: string): string {
        return SafeJSON.get('lastName', countyData);
    }
    public getLicenseeLastFirstMiddleNameFromCounty(countyData: string): string {
        return  this.getLicenseeLastNameFromCounty(countyData) + ', ' +
                this.getLicenseeFirstNameFromCounty(countyData) + ' ' +
                this.getLicenseeMiddleNameFromCounty(countyData);
    }
      /******************************************************
     RUNNER
        ACTIVE 		    300070
        INACTIVE 		400071

     BAIL AGENT
        ACTIVE 		    201
        INACTIVE 		120

     Returns bondsman's status of the company they work for.
     Used for both the employee and the employer's status.
     *******************************************************/
    public getAgentForStatus(countyData: any): string {
        return SafeJSON.get('agentForStatus', countyData);
    }
    public getAgentForStatusDate(countyData: any): string {
        return SafeJSON.get('agentForStatusDate', countyData);
    }
    // Not used right now
    // public getLicenseeProhibitReasonsList(): Array<string> {
       // return Array.from(this.json.licensee.runnerList.prohibitedReasonList.values());
    // }
    // public getBailAgentLicenseProhibitReasonsList(countyData: any): Array<string> {
        // return new Array(); // Array.from(agent.prohibitedReasonList.values());
    // }
    public getRunnerProhibitReasonsList(countyData: any): Array<string> {
        let reasonsList = new Array();

        const licenseType = SafeJSON.get('licenseType', countyData);
        if (licenseType !== this.RUNNER) {
            return [];
        }
        let status = this.getDoiStatusByType(licenseType);
        if (status === this.PROHIBITED || status === this.INACTIVE) {
            const doiStatusDate = this.getDoiStatusDateByType(licenseType);
            let statusText = 'Department of Insurance License Status: ' + status + ' since ' + this.convertDate(doiStatusDate);
            reasonsList.push(statusText);
        }
        status = this.getAgentForStatus(countyData);
        if (status === this.INACTIVE) {
            const date = this.getAgentForStatusDate(countyData);
            let statusText = 'Runner Status for Professional Bondsman: ' + status + ' since ' + this.convertDate(date);
            reasonsList.push(statusText);
        }
        status = this.getStatewideRegistryStatus();
        if (status === this.JUDGEMENT_PENDING) {
            const date = this.getStatewideRegistryStatusDate();
            let statusText = 'Statewide Registry Status: ' + status + ' since ' + this.convertDate(date);
            reasonsList.push(statusText);
        }
        return reasonsList;
    }
    public getProfessionalBondsmanLicenseeProhibitReasonsList(countyData: string): Array<string> {
        let reasonsList = new Array();

        const licenseType = SafeJSON.get('licenseType', countyData);
        if (licenseType !== this.PROFESSIONAL_BONDSMAN) {
           return [];
        }
        let status = this.getDoiStatusForCounty(countyData);
        if (status === this.PROHIBITED || status === this.INACTIVE) {
            const doiStatusDate = this.getDoiStatusForCountyDate(countyData);
            let statusText = 'Department of Insurance License Status: ' + status + ' since ' + this.convertDate(doiStatusDate);
            reasonsList.push(statusText);
        }
        status = this.getStatewideRegistryStatus();
        if (status === this.JUDGEMENT_PENDING) {
            const date = this.getStatewideRegistryStatusDate();
            let statusText = 'Statewide Registry Status: ' + status + ' since ' + this.convertDate(date);
            reasonsList.push(statusText);
        }
        status = this.getCountyStatus(countyData);
        if (status === this.JUDGEMENT_PENDING) {
            const date = this.getCountyStatusDate(countyData);
            let statusText = 'County Registry Status: ' + status + ' since ' + this.convertDate(date);
            reasonsList.push(statusText);
        }
        return reasonsList;
    }
    public getBailAgentProhibitReasonsList(countyData: any): Array<string> {
        let reasonsList = new Array();

        const licenseType = SafeJSON.get('licenseType', countyData);
        if (licenseType !== this.BAIL_AGENT) {
            return [];
        }
        let status = this.getDoiStatusByType(licenseType);
        if (status === this.PROHIBITED || status === this.INACTIVE) {
            const doiStatusDate = this.getDoiStatusDateByType(licenseType);
            let statusText = 'Department of Insurance License Status: ' + status + ' since ' + this.convertDate(doiStatusDate);
            reasonsList.push(statusText);
        }
        status = this.getAgentForStatus(countyData);
        if (status === this.INACTIVE) {
            const date = this.getAgentForStatusDate(countyData);
            let statusText = 'Status with Listed Insurance Company: ' + status + ' since ' + this.convertDate(date);
            reasonsList.push(statusText);
        }
        status = this.getCountyRegistryStatus(countyData);
        if (status === this.JUDGEMENT_PENDING) {
            const date = this.getCountyRegistryStatusDate(countyData);
            let statusText = 'Statewide Registry Status: ' + status + ' since ' + this.convertDate(date);
            reasonsList.push(statusText);
        }
        return reasonsList;
    }
    public getInsuranceCompanyProhibitReasonsList(countyData: string): Array<string> {
        let reasonsList = new Array();

        const licenseType = SafeJSON.get('licenseType', countyData);
        if  (licenseType !== this.SURETY_INSURANCE_COMPANY) {
            return [];
        }
        let status = this.getDoiStatusForCounty(countyData);
        if (status === this.PROHIBITED || status === this.INACTIVE) {
            const doiStatusDate = this.getDoiStatusForCountyDate(countyData);
            let statusText = 'Department of Insurance License Status: ' + status + ' since ' + this.convertDate(doiStatusDate);
            reasonsList.push(statusText);
        }
        status = this.getCountyRegistryStatus(countyData);
        if (status === this.PROHIBITED) {
            const date = this.getCountyRegistryStatusDate(countyData);
            let statusText = 'Statewide Registry Status: ' + status + ' since ' + this.convertDate(date);
            reasonsList.push(statusText);
        }
        status = this.getCountyStatus(countyData);
        if (status === this.JUDGEMENT_PENDING) {
            const date = this.getCountyStatusDate(countyData);
            let statusText = 'County Registry Status: ' + status + ' since ' + this.convertDate(date);
            reasonsList.push(statusText);
        }
        return reasonsList;
    }
    public getDerivedStatusToPostBondsByAgentOrRunner(countyData: string): string {
        return SafeJSON.get('derivedStatusToPostBondsByAgentOrRunner', countyData);
    }
    public getDerivedStatusToPostBondsByAgentOrRunnerDate(countyData: string): string {
        return SafeJSON.get('derivedProhibitionDateForAgentOrRunner', countyData);
    }
    public getDerivedStatusToPostBondsByProfBondsmanOrInsCo(countyData: string): string {
        return SafeJSON.get('derivedStatusToPostBondsByProfBondsmanOrInsCo', countyData);
    }
    public getDerivedStatusToPostBondsByProfBondsmanOrInsCoDate(countyData: string): string {
        return SafeJSON.get('derivedProhibitionDateForProfBondsmanOrInsCo', countyData);
    }
    public getCountyStateLicenseNumber(countyData: string): number {
        return SafeJSON.get('agentForStateLicenseId', countyData);
    }
    /********************************************************************
    RUNNER
        ACTIVE 		    300070
        INACTIVE 	    400083
        PROHIBITED 	    3469868

    BAIL AGENT
        ACTIVE 		    201
        INACTIVE 	    199
        PROHIBITED 	    221
    Returns the Department of Insurance status for this licensee (statewide)
    ************************************************************************/
    public getDoiStatusForStatewide(licenseTypesData): string {
        const status = SafeJSON.get('doiStatus', licenseTypesData);
        // console.log('doiStatus=' + status);
        return status;
    }
    public getDoiStatusDateForStatewide(licenseTypesData): string {
        const statusDate = SafeJSON.get('doiStatusDate', licenseTypesData);
        return statusDate;
    }
    // public getProhitiedRunnerReason(reason: any): string {
        // return SafeJSON.get('reasonDescription', reason);
    // }
    // public getProfessionalBondsmanLicenseeProhibitReason(reason: any): string {
        // return SafeJSON.get('reasonDescription', reason);
    // }
    //////////////////////////////////////////////////////////////////////////////////////
    // Bond Forfeitures
    public getForfeitureCaseNumber(forfeiture: any): string {
        return SafeJSON.get('caseNumber', forfeiture);
    }
    public getForfeitureCounty(forfeiture: any): string {
        let county = SafeJSON.get('county', forfeiture);
        return county;
    }
    public getForfeiturePleading(forfeiture: any): string {
        return SafeJSON.get('filingType', forfeiture);
    }
    public getForfeitureJudgment(forfeiture: any): string {
        return SafeJSON.get('judgmentText', forfeiture);
    }
    public getForfeitureJudgmentDate(forfeiture: any): string {
        return SafeJSON.get('judgmentDate', forfeiture);
    }
    public getForfeitureBondDate(forfeiture: any): string {
        return SafeJSON.get('bondDate', forfeiture);
    }
    public getForfeitureBondAmount(forfeiture: any): number {
        return SafeJSON.get('bondAmount', forfeiture);
    }
    public getForfeitureLicenseType(forfeiture: any): string {
        return SafeJSON.get('suretyRole', forfeiture);
    }
    public getForfeitureOtherLicense(forfeiture: any): string {
        return SafeJSON.get('otherSuretyNumber', forfeiture);
    }
    public getForfeitureMotion(forfeiture: any): string {
        return SafeJSON.get('motionType', forfeiture);
    }
    /////////////////////////////////////////////////////////////////////
    // Utilities
    convertDate(date: string): string {
        let datePipe = new DatePipe('en-US');
        return datePipe.transform(date, 'MM/dd/yyyy');
     }
     convertTime(time: string): string {
       let datePipe = new DatePipe('en-US');
       return datePipe.transform(time, 'shortTime');
     }
     logToConsoleAndServer(text: string) {
        // JL().error(text);
        console.log(text);
    }
    getLastUpdatedDate(): string {
        let dateTS = this.searchRecordLastUpdateDate;
        let date = this.convertDate(dateTS);
        return date;
    }
    getLastUpdatedTime(): string {
      let dateTS = this.searchRecordLastUpdateDate;
      let time = this.convertTime(dateTS);
      return time;
    }
}



// WEBPACK FOOTER //
// /home/jenkins/workspace/pipeline-ciprs-ui-final/project/src/app/bondsman/bondsman.ts